import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Fade,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { AuthContext } from "../../../context/AuthContext/AuthContext";

// Duración del pre-auth token según backend: 2 minutos exactos.
const PREAUTH_TTL_SECONDS = 120;

/**
 * Pantalla de verificación TOTP durante el login.
 * Se muestra cuando /api/AuthV2/login retorna mfaRequired=true.
 *
 * Props:
 *   onSuccess(userData) — callback tras verificación exitosa; recibe userData
 *     completo para que LoginPage maneje navegación (change-password, role
 *     selector o /Main).
 *   onUseRecoveryCode() — cambia al step de código de recuperación.
 *   onExpired() — cuando expira el pre-auth token (2 min) — LoginPage debe
 *     limpiar preAuth y volver al step de login con mensaje.
 */
const MfaVerifyForm = ({ onSuccess, onUseRecoveryCode, onExpired }) => {
  const { verifyMfaCode } = useContext(AuthContext);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(PREAUTH_TTL_SECONDS);

  // Countdown del pre-auth token
  useEffect(() => {
    if (remaining <= 0) {
      onExpired?.();
      return;
    }
    const timer = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onExpired?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [remaining, onExpired]);

  const handleSubmit = useCallback(async () => {
    setError(null);
    if (code.length !== 6) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const userData = await verifyMfaCode(code);
      onSuccess?.(userData);
    } catch (err) {
      const result = err?.response?.data?.result;
      const message = err?.response?.data?.message || err?.message;
      if (result === 4) {
        setError("Invalid code. Please check your authenticator app and try again.");
      } else if (result === 2) {
        setError("2FA is not active for this user.");
      } else {
        setError(message || "Could not verify the code. Try again.");
      }
      setCode("");
    } finally {
      setLoading(false);
    }
  }, [code, verifyMfaCode, onSuccess]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && code.length === 6 && !loading) {
      handleSubmit();
    }
  };

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const timeColor = remaining <= 30 ? "#d32f2f" : remaining <= 60 ? "#ed6c02" : "#666";

  return (
    <Fade in timeout={400}>
      <Box>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <ShieldOutlinedIcon sx={{ fontSize: 44, color: "#0C343D", mb: 0.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0C343D" }}>
            Two-Factor Verification
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
            Enter the 6-digit code from your authenticator app.
          </Typography>
        </Box>

        {/* Countdown */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: timeColor,
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          Code expires in {minutes}:{seconds.toString().padStart(2, "0")}
        </Typography>

        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}

        <TextField
          autoFocus
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={handleKeyDown}
          placeholder="000000"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 6,
            style: {
              textAlign: "center",
              fontSize: "1.75rem",
              letterSpacing: "0.5rem",
              fontFamily: "monospace",
              fontWeight: 600,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "#fafafa",
              "&:hover fieldset": { borderColor: "#00bcd4" },
              "&.Mui-focused fieldset": { borderColor: "#0C343D" },
            },
          }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={code.length !== 6 || loading}
          sx={{
            mt: 2.5,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "none",
            bgcolor: "#0C343D",
            boxShadow: "0 4px 12px rgba(12,52,61,0.3)",
            "&:hover": {
              bgcolor: "#0a2a33",
              boxShadow: "0 6px 16px rgba(12,52,61,0.4)",
            },
            "&.Mui-disabled": { bgcolor: "#ccc" },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Verify"}
        </Button>

        <Typography
          variant="body2"
          sx={{ mt: 2, textAlign: "center", color: "#666" }}
        >
          <Link
            component="button"
            type="button"
            onClick={onUseRecoveryCode}
            sx={{
              color: "#0C343D",
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline", color: "#00bcd4" },
            }}
          >
            Don't have your phone? Use a recovery code
          </Link>
        </Typography>
      </Box>
    </Fade>
  );
};

export default MfaVerifyForm;
